import { Question, Section } from "@/types/resources";

import { isCellFilled } from "./cells";
import { getUseCaseRequirements } from "./useCaseRequirements";

/** True when the list holds at least one non-blank entry — blank strings don't
    count as a valid option/line. */
const hasContent = (values: string[]): boolean =>
  values.some((value) => (value ?? "").trim() !== "");

/**
 * Whether a question is missing the content it needs to run — the same
 * condition that surfaces the red "Add options/rows" hint and red left rail in
 * the question card. Blank (empty-string) options/lines don't count; image-mode
 * choice questions count their media as options.
 *
 * `category_assignment` also errors when a track has no destination once a
 * later question exists to route to — we surface that in red rather than
 * blocking save, so authors can add the downstream questions first and come
 * back. `allQuestions` is needed to know whether any downstream target exists.
 */
export const questionHasError = (
  question: Question,
  allQuestions: Question[] = []
): boolean => {
  switch (question.type) {
    case "single_choice":
    case "multiple_choice":
      // Options carried from a prior question are resolved at run time, so an
      // empty list isn't an error — the carry-forward provides them.
      return (
        question.carry_forward?.target !== "options" &&
        !hasContent(question.options) &&
        (question.media_urls?.length ?? 0) === 0
      );
    case "category_assignment": {
      if (!hasContent(question.options)) return true;
      const order = "order" in question ? question.order : 0;
      const hasDownstream = allQuestions.some((q) => q.order > order);
      if (!hasDownstream) return false;
      const rules = "rules" in question ? question.rules : [];
      const routed = new Set(
        rules
          .filter(
            (r) => r.source_question_id === null && r.option_index !== null
          )
          .map((r) => r.option_index)
      );
      return question.options.some((_, i) => !routed.has(i));
    }
    case "ranking":
      return !hasContent(question.lines);
    case "single_select_matrix":
    case "multi_select_matrix": {
      // A matrix can carry either dimension (rows or columns) from a prior
      // question; the carried one is resolved at run time, so only the
      // manually-authored dimension must have content here.
      const target = question.carry_forward?.target;
      const linesMissing = target !== "lines" && !hasContent(question.lines);
      const optionsMissing =
        target !== "options" && !hasContent(question.options);
      return optionsMissing || linesMissing;
    }
    default:
      return false;
  }
};

/**
 * Whether a section expects cells but has none filled — the condition behind
 * the red "No X added" hint and red left rail on the section card. Item-pool
 * use cases (Preference, Awareness, MaxDiff, TURF) author their items on the
 * questions instead, so they never report an error here.
 */
export const sectionHasError = (section: Section): boolean => {
  const requirements = getUseCaseRequirements(section.use_case);
  const variables = section.config?.variables ?? [];
  const expectsCells =
    section.use_case === null
      ? variables.some((v) => v.name.trim())
      : requirements.min_cells > 0;
  if (!expectsCells) return false;
  return section.cells.every((cell) => !isCellFilled(cell, variables));
};
